/**
 * Client-side search over the whole archive.
 *
 * The corpus is ~780 records and ~38 kB of text, so the index ships to the
 * browser rather than the query travelling to the database. That buys three
 * things a server-side search could not: results on every keystroke with no
 * round trip, coverage of everything rather than only the pages already
 * scrolled, and no per-query cost.
 *
 * **Staleness.** The index is content-addressed by the `version` the server
 * derives from `max(updated_at)` and row counts. A cached index is used
 * immediately so search is instant, and the version is re-checked in the
 * background; if it moved, the index is rebuilt and the results update.
 * Editors see their changes within a minute without anyone reloading.
 */

import MiniSearch from "minisearch";

import { apiFetch } from "@/lib/api";

export interface SearchIndexEntry {
  id: string;
  /** Space-joined participant names. */
  people: string;
  showName: string | null;
  /** Space-joined tag names, the show's and the episode's merged. */
  tags: string;
  title: string;
}

export interface SearchIndexResponse {
  entries: SearchIndexEntry[];
  version: string;
}

export interface SearchResult extends SearchIndexEntry {
  score: number;
}

/**
 * Field weights.
 *
 * A title match is what someone almost always means; a tag match is nearly
 * as deliberate — nobody types "christmas" by accident; a host's name is a
 * strong but less direct signal; a show description is the weakest, since a
 * long blurb can accidentally contain almost any word.
 */
const BOOST = { people: 2, showName: 1.5, tags: 3, title: 4 };

const buildIndex = (entries: SearchIndexEntry[]) => {
  const index = new MiniSearch<SearchIndexEntry>({
    fields: ["title", "showName", "tags", "people"],
    searchOptions: {
      boost: BOOST,
      // Typo tolerance scaled to term length: a 4-letter word tolerates no
      // edits, a 10-letter one tolerates two. A flat distance makes short
      // queries match almost anything.
      fuzzy: (term) => (term.length > 5 ? 0.2 : 0),
      prefix: true,
    },
    storeFields: ["id", "people", "showName", "tags", "title"],
  });
  index.addAll(entries);
  return index;
};

interface LoadedIndex {
  index: MiniSearch<SearchIndexEntry>;
  version: string;
}

let cached: LoadedIndex | null = null;
let inFlight: Promise<LoadedIndex | null> | null = null;

const fetchIndex = async () => {
  const data = await apiFetch<SearchIndexResponse>("/api/public/search-index");
  if (!data) {
    return null;
  }
  cached = { index: buildIndex(data.entries), version: data.version };
  return cached;
};

/**
 * The index, built at most once per version. Concurrent callers share one
 * request rather than each pulling and indexing their own copy.
 */
export const loadSearchIndex = async () => {
  if (cached) {
    return cached;
  }
  inFlight ??= fetchIndex();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
};

/**
 * Rebuilds the index if the server's content fingerprint has moved. Cheap
 * enough to call on focus: the version endpoint returns a short string.
 */
export const refreshSearchIndexIfStale = async () => {
  if (!cached) {
    return await loadSearchIndex();
  }
  const latest = await apiFetch<{ version: string }>(
    "/api/public/search-index/version"
  );
  if (latest && latest.version !== cached.version) {
    cached = null;
    return await loadSearchIndex();
  }
  return cached;
};

export const searchArchive = (
  index: MiniSearch<SearchIndexEntry>,
  query: string,
  limit = 50
): SearchResult[] => {
  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }
  return index
    .search(trimmed)
    .slice(0, limit)
    .map((hit) => ({
      id: hit.id as string,
      people: (hit.people as string) ?? "",
      score: hit.score,
      showName: (hit.showName as string | null) ?? null,
      tags: (hit.tags as string) ?? "",
      title: hit.title as string,
    }));
};
